/**
 * Admin Panel — Echo configuration API
 *
 * Cross-org admin-scoped read surface for Echo. Global Echo settings
 * (like export artifact retention) live in `SystemConfig` under the
 * `echo` category — a single value applied to every organization,
 * not a per-org field. This domain therefore carries only the log
 * query endpoint today.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { EchoLogRowSchema } from "../echo/zecho";

// ─── Schemas ────────────────────────────────────────────────────

/**
 * Path vars for a per-org read: the target organization's ID.
 *
 * Not a UUID — Nexus uses the Clerk organization id (shape `org_<slug>`)
 * as the primary key on the Organization table. See `schema.prisma`:
 * `Organization.id` has no default, it is written explicitly at sync
 * time from Clerk. Same convention every other admin controller follows
 * (see `admin-organizations.controller.ts`).
 */
const OrgIdPathVarsSchema = z.object({
  orgId: z.string().min(1)
});

/**
 * Query params for the cross-org log list. Mirrors `/api/echo/logs` params
 * except there is no tenant gate from the JWT — the orgId is carried on
 * the path, not inferred from the caller's session. Every field is
 * optional; the default page is 100 most-recent rows.
 */
const QueryLogsParamsSchema = z.object({
  eventTypes: z.array(z.string()).optional(),
  resourceTypes: z.array(z.string()).optional(),
  resourceId: z.string().optional(),
  actorId: z.string().optional(),
  severity: z.array(z.string()).optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(10_000).optional().default(100),
  offset: z.coerce.number().int().min(0).optional().default(0)
});

const QueryLogsResponseSchema = z.object({
  logs: z.array(EchoLogRowSchema),
  total: z.number().int().min(0),
  limit: z.number().int().min(1),
  offset: z.number().int().min(0)
});

// ─── Endpoint definitions ───────────────────────────────────────

export const ZAdminEcho = {
  /**
   * Cross-org log viewer — list the audit events for any organization.
   * Params mirror the regular `/api/echo/logs` endpoint so the admin UI
   * can reuse the same filter grammar.
   * GET /api/admin/echo/organizations/:orgId/logs
   */
  QueryLogs: {
    method: "GET" as const,
    path: "/api/admin/echo/organizations/:orgId/logs",
    PathVars: OrgIdPathVarsSchema,
    Params: QueryLogsParamsSchema,
    Response: ApiSuccessSchema(QueryLogsResponseSchema)
  }
} as const;

// ─── Convenience type exports ──────────────────────────────────

export type AdminEchoQueryLogsParams = z.infer<typeof QueryLogsParamsSchema>;
export type AdminEchoQueryLogsResponse = z.infer<typeof QueryLogsResponseSchema>;
